import { getUserGetMyProfile, postUserUpdateMyProfile } from '@apis/Customers';
import { useCallback, useEffect, useState } from 'react';
import { useDi } from '@root/Services/DI';
import { Box, Button, Group, Loader, LoadingOverlay, Space, Stack, Text, TextInput } from '@mantine/core';
import { Clearfix } from '@root/Design/Primitives';
import { UserImage } from '@root/Components/Users/UserImage';
import { User } from '@apis/Customers/model';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { SidePanel, SidePanelOpener } from '@root/Design/SidePanel';
import { useEvent, useEventValue } from '@root/Services/EventEmitter';
import { BasicRouteLoader } from '@root/Services/Router/BasicRouteLoader';

interface UserProfileDrawerProps {
    sidePanelOpener: SidePanelOpener;
    onChanged?: (profile: User) => void;
}

export function UserProfile({ sidePanelOpener, onChanged }: UserProfileDrawerProps) {
    const [profile, setProfile] = useState<User>();
    const [loading, setLoading] = useState<boolean>(false);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
    });
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [saving, setSaving] = useState(false);
    const notificationSvc = useDi(NotificationService);
    const router = useDi(BasicRouteLoader);
    useEvent(router.routeMeta);
    const currentRoute = router.getTopRouteMeta().currentPathWithoutParams;

    const loadProfile = useCallback(async () => {
        setLoading(true);
        try {
            const user = await getUserGetMyProfile();
            setProfile(user);
            setForm({
                firstName: user?.FirstName ?? '',
                lastName: user?.LastName ?? '',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const onOpenEvt = useCallback((opened: boolean) => {
        if (opened) {
            loadProfile();
        }
    }, []);
    useEvent(sidePanelOpener.evt, onOpenEvt);

    const handleInputChange = (field: keyof typeof form, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        if (profile) {
            if (form.firstName !== profile.FirstName || form.lastName !== profile.LastName) {
                setIsDirty(true);
            } else {
                setIsDirty(false);
            }
        }
    }, [form, profile]);

    const save = async () => {
        setSaving(true);
        const userCopy = { ...profile, FirstName: form.firstName, LastName: form.lastName };
        try {
            const updatedUser = await postUserUpdateMyProfile(userCopy);
            setProfile(updatedUser);
            onChanged?.(updatedUser);
            notificationSvc.notify('Profile Saved', '', 'success', null);
        } catch (error) {
            notificationSvc.notify('Profile Save Failed', '', 'error', null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <SidePanel
                key={currentRoute}
                size={400}
                opener={sidePanelOpener}
                title="Edit User Profile"
                toolbar={
                    <>
                        <Button variant="outline" onClick={sidePanelOpener.close}>
                            Cancel
                        </Button>
                        <Button disabled={!isDirty} onClick={save} rightIcon={saving ? <Loader size="sm" color="gray.0" /> : null}>
                            Save
                        </Button>
                    </>
                }
            >
                <Stack sx={{ height: '100%' }}>
                    <>
                        <LoadingOverlay visible={loading} overlayBlur={2} />
                        <Group>
                            <Box>
                                <UserImage
                                    firstName={profile?.FirstName ?? ''}
                                    lastName={profile?.LastName ?? ''}
                                    email={profile?.EMail ?? ''}
                                    picture={profile?.Picture ?? ''}
                                    id={profile?.Id ?? 0}
                                    diameter={48}
                                />
                            </Box>
                            <div>
                                {(profile?.FirstName &&
                                    profile?.LastName &&
                                    profile?.FirstName.trim().length > 0 &&
                                    profile?.LastName.trim().length > 0) ||
                                profile?.EMail ? (
                                    <Text weight="bold">{profile?.FirstName + ' ' + profile?.LastName}</Text>
                                ) : (
                                    <Text weight="dim" italic>
                                        No Name
                                    </Text>
                                )}
                                <Text>{profile?.EMail}</Text>
                            </div>
                        </Group>
                        <Clearfix />
                        <Space h="md" />
                        <TextInput
                            label="First Name"
                            placeholder="First Name"
                            defaultValue={form.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                        />
                        <Space h="md" />
                        <TextInput
                            label="Last Name"
                            placeholder="Last Name"
                            defaultValue={form.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />{' '}
                    </>
                </Stack>
            </SidePanel>
        </>
    );
}
